import CheckCircle from 'assets/check-circle.svg';
import { ReactComponent as Information } from 'assets/exclamation-circle-fill.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import PasswordWithEye from 'components/PasswordWithEye';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useLoadingMask } from 'contexts/LoadingMaskProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { SignInDto } from 'dto/signin.dto';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Validator from 'utils/validator';

const UnbindCloudAccount = ({ resetForm, updateUserProfile }: { resetForm: VoidFunction; updateUserProfile: VoidFunction }) => {
  const { t } = useTranslation();
  const { defaultUserEmail } = useConfig();
  const { postUsersUnBindCloudAccout } = useAuth();
  const { addToast } = useToast();
  const { addConfirmModal, addCustomModal, closeCustomModal } = useModal();
  const { addLoadingMask } = useLoadingMask();

  const UnbindCloudAccountContent = () => {
    const methods = useForm<SignInDto>({ defaultValues: { email: '', password: '' } });

    const onClickCancel = () => {
      resetForm();
      closeCustomModal();
    };

    const onSubmitUnbindCloudAccount = (data: SignInDto) => {
      addLoadingMask({
        show: true,
      });
      postUsersUnBindCloudAccout(data)
        .then(() => {
          addToast({
            title: t('toast.unbindingSuccessful'),
            message: '',
            color: 'bg-toast-success-50',
            icon: CheckCircle,
            text: 'text-toast-success-800',
            svg: 'toastIcon-success',
          });
          updateUserProfile();
        })
        .catch((error) => {
          let message: string = error.response?.data.message ?? '';
          addToast({
            title: t('toast.unbindingFailed'),
            message,
            color: 'bg-toast-error-50',
            icon: ErrorCircle,
            text: 'text-toast-error-800',
            svg: 'toastIcon-error',
          });
        })
        .finally(() => {
          resetForm();
          closeCustomModal();
          addLoadingMask({
            show: false,
          });
        });
    };

    return (
      <>
        <Information className="w-10 h-10 inline mr-2 rounded-full fill-blue-400 bg-blue-50 p-3" />
        <h3 className="font-bold text-lg inline">{t('modal.titleUnbindCloudAccount')}</h3>
        <div className="py-4 pl-12 pr-2">
          <div className="flex flex-col gap-6 text-sm">
            <p>{t('modal.unbindCloudAccountMessage')}</p>
          </div>
          <FormProvider {...methods}>
            <form className="pt-4 text-xs flex flex-col gap-3" onSubmit={methods.handleSubmit(onSubmitUnbindCloudAccount)}>
              <div className="flex flex-col space-y-1 flex-1 w-full">
                <label>{t('profile.formEmail')}</label>
                <input
                  type="text"
                  className={'input w-full' + (methods.formState.errors.email ? ' input-error' : '')}
                  {...methods.register('email', {
                    required: Validator().required,
                    pattern: Validator().email,
                    validate: {
                      defaultUserEamilDuplicate: (v) => v !== defaultUserEmail || `${t('login.defaultUserEamilDuplicate')} ${defaultUserEmail}`,
                    },
                  })}
                />
                {methods.formState.errors.email && <span className="span-error">{methods.formState.errors.email.message}</span>}
              </div>
              <div className="flex flex-col space-y-1 flex-1 w-full">
                <label>{t('login.formPassword')}</label>
                <PasswordWithEye
                  register={{
                    ...methods.register('password', {
                      required: Validator().required,
                      minLength: Validator().minLength(8, 63),
                      maxLength: Validator().maxLength(8, 63),
                      pattern: Validator().userPassword,
                    }),
                  }}
                  errorsName={methods.formState.errors.password}
                  size="w-full"
                />
                {methods.formState.errors.password && <span className="span-error">{methods.formState.errors.password.message}</span>}
                <p className="text-xs text-gray-500">{t('profile.bindCloudAccoutPasswordRule')}</p>
              </div>
              <div className="flex space-x-2 justify-end">
                <button type="button" className="btn btn-outline" onClick={onClickCancel}>
                  {t('modal.btnCancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={!methods.formState.isDirty}>
                  {t('modal.btnRemove')}
                </button>
              </div>
            </form>
          </FormProvider>
        </div>
      </>
    );
  };

  const onClickConfirmUnbind = () => {
    addCustomModal({
      open: true,
      content: <UnbindCloudAccountContent />,
    });
  };

  useEffect(() => {
    addConfirmModal({
      open: true,
      id: 'unbindCloudConfirmModal',
      btnOk: t('modal.btnOk'),
      btnCancel: t('modal.btnCancel'),
      type: 'info',
      title: t('modal.titleUnbindCloudConfirmation'),
      message: (
        <div className="flex flex-col gap-6 text-sm">
          <p>{t('modal.unbindCloudAccountConfirmationMessageFront')}</p>
          <p>{t('modal.unbindCloudAccountConfirmationMessageBack')}</p>
        </div>
      ),
      onClickOk: () => onClickConfirmUnbind(),
      onClickCancel: () => resetForm(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};
export default UnbindCloudAccount;
